import { PartialRecord } from "@/types/common";
import type { ActionResource } from "@/types/rights";
import { rightsActions } from "@lib/rights";
import { EventModuleEntityRightsTypes } from "@features/events/types/rigths";
import {
  RoleAssignmentPermissions,
  TeamMemberRoleGroupColor,
  TeamMemberRoleGroupOrder,
  TeamMemberRoleGroups,
  TeamMemberRoleScopeMap,
  TeamMemberRolesArray,
  TeamMemberRolesOrderMap,
} from "@features/teams/teams-consts";
import {
  TeamMemberAggregateEntity,
  TeamMemberAggregateOutputView,
  TeamMemberEntity,
  TeamMemberOutputView,
  TeamMemberRole,
  TeamMemberRoleGroup,
  TeamMemberScope,
} from "@features/teams/types/team";
import {
  TeamMemberAggregateRightsTypes,
  TeamMemberEntityRightsTypes,
} from "@features/teams/types/rights";

const EventTeamMemberActionResources = [
  "event/team/member",
  "team/member",
] satisfies ActionResource[];

const EventTeamMemberBiographyActionResources = [
  "event/team/member/biography",
  "team/member/biography",
] satisfies ActionResource[];

const getHighestRoleOrder = (member: Pick<TeamMemberEntity, "roles">): number => {
  if (!member.roles?.length) {
    return 0;
  }

  return member.roles.reduce((highestOrder, role) => {
    const orderEntry = TeamMemberRolesOrderMap.find((entry) =>
      (entry.roles as readonly TeamMemberRole[]).includes(role),
    );
    return orderEntry && orderEntry.order > highestOrder ? orderEntry.order : highestOrder;
  }, 0);
};

const addRightsToTeamMember = (data: TeamMemberOutputView): TeamMemberEntity =>
  rightsActions.parseActions<TeamMemberOutputView, TeamMemberEntityRightsTypes, TeamMemberEntity>(
    data,
    (actions, fns) => ({
      ...fns.canEntityRightsForAnyResource(actions, ["member", "team/member"]),
    }),
  );

const addRightsToTeamMemberAggregate = (
  data: TeamMemberAggregateOutputView,
): TeamMemberAggregateEntity => {
  const aggregate = rightsActions.parseActions<
    TeamMemberAggregateOutputView,
    TeamMemberAggregateRightsTypes,
    TeamMemberAggregateEntity
  >(data, (actions, fns) => ({
    ...fns.canEntityRightsForAnyResource(actions, EventTeamMemberActionResources),
    canUploadPhoto: fns.canAny(actions, EventTeamMemberActionResources, "upload-photo"),
    canListBiography: fns.canAny(actions, EventTeamMemberBiographyActionResources, "list"),
    canSendNotification: false,
    canGetDiff: fns.can(actions, "diff-content", "get"),
  }));

  return {
    ...aggregate,
    member: aggregate.member ? addRightsToTeamMember(aggregate.member) : undefined,
  };
};

export interface GroupedTeamMemberRoles {
  readonly group: TeamMemberRoleGroup;
  readonly color: (typeof TeamMemberRoleGroupColor)[TeamMemberRoleGroup];
  readonly roles: TeamMemberRole[];
}

const getRolesForScope = (scope: TeamMemberScope): TeamMemberRole[] => [
  ...(TeamMemberRoleScopeMap[scope] as readonly TeamMemberRole[]),
];

const getRoleGroup = (role: TeamMemberRole): TeamMemberRoleGroup => {
  const roleGroup = TeamMemberRoleGroupOrder.find((group) =>
    (TeamMemberRoleGroups[group] as readonly TeamMemberRole[]).includes(role),
  );

  if (!roleGroup) {
    throw new Error(`Team member role group is not configured for role: ${role}`);
  }

  return roleGroup;
};

const roleOrder = (role: TeamMemberRole): number => {
  const orderEntry = TeamMemberRolesOrderMap.find((entry) =>
    (entry.roles as readonly TeamMemberRole[]).includes(role),
  );

  return orderEntry ? orderEntry.order : 0;
};

const roleIndex = (role: TeamMemberRole): number =>
  (TeamMemberRolesArray as readonly TeamMemberRole[]).indexOf(role);

const sortRolesByPriority = (roles: readonly TeamMemberRole[]): TeamMemberRole[] =>
  [...roles].sort((a, b) => roleOrder(b) - roleOrder(a) || roleIndex(a) - roleIndex(b));

const groupRolesByDepartment = (roles: readonly TeamMemberRole[]): GroupedTeamMemberRoles[] =>
  TeamMemberRoleGroupOrder.map((group) => {
    const groupRoles = sortRolesByPriority(roles.filter((role) => getRoleGroup(role) === group));

    return groupRoles.length > 0
      ? {
          group,
          color: TeamMemberRoleGroupColor[group],
          roles: groupRoles,
        }
      : undefined;
  }).filter((group): group is GroupedTeamMemberRoles => Boolean(group));

const canPermissionApplyToScope = (
  permissionScope: TeamMemberScope,
  targetScope: TeamMemberScope,
): boolean =>
  permissionScope === targetScope ||
  permissionScope === "WORKSPACE" ||
  (permissionScope === "PROJECT" && targetScope === "EVENT");

const getAssignableRolesForScope = (
  targetScope: TeamMemberScope,
  currentUserRoles: readonly TeamMemberRole[] = [],
): TeamMemberRole[] => {
  const availableRoles = new Set(getRolesForScope(targetScope));
  const assignableRoles = new Set<TeamMemberRole>();

  currentUserRoles.forEach((currentUserRole) => {
    const permissions = RoleAssignmentPermissions[currentUserRole];

    if (!permissions || !canPermissionApplyToScope(permissions.scope, targetScope)) {
      return;
    }

    permissions.canAssign.forEach((role) => {
      if (availableRoles.has(role)) {
        assignableRoles.add(role);
      }
    });
  });

  return sortRolesByPriority([...assignableRoles]);
};

const filterAssignableRoles = ({
  targetScope,
  currentUserRoles,
}: {
  readonly targetScope: TeamMemberScope;
  readonly currentUserRoles?: readonly TeamMemberRole[];
}): GroupedTeamMemberRoles[] =>
  groupRolesByDepartment(getAssignableRolesForScope(targetScope, currentUserRoles));

const emptyRolesByScope = (): Record<TeamMemberScope, TeamMemberRole[]> => ({
  WORKSPACE: [],
  PROJECT: [],
  EVENT: [],
});

const resolveCurrentUserRolesByScope = (
  members: readonly Pick<TeamMemberEntity, "enabled" | "isMe" | "roles" | "scope">[],
): Record<TeamMemberScope, TeamMemberRole[]> => {
  const rolesByScope = emptyRolesByScope();

  members.forEach((member) => {
    if (!member.isMe || !member.enabled || !member.scope) {
      return;
    }

    rolesByScope[member.scope] = sortRolesByPriority([
      ...new Set([...rolesByScope[member.scope], ...(member.roles || [])]),
    ]);
  });

  return rolesByScope;
};

const canListEventTeamMembers = (
  event?: {
    modulesRights?: PartialRecord<EventModuleEntityRightsTypes, boolean>;
  } | null,
): boolean => event?.modulesRights?.canListMembers === true;

export const teamsTools = {
  canListEventTeamMembers,
  isEnabledFilter: (data: { enabled?: boolean }) => Boolean(data.enabled),
  addRightsToTeamMember,
  addRightsToTeamMemberAggregate,
  sortByRole: (a: Pick<TeamMemberEntity, "roles">, b: Pick<TeamMemberEntity, "roles">) =>
    getHighestRoleOrder(b) - getHighestRoleOrder(a),
  getRolesForScope,
  getRoleGroup,
  groupRolesByDepartment,
  getAssignableRolesForScope,
  filterAssignableRoles,
  resolveCurrentUserRolesByScope,
};
